import { useState } from 'react';
import { Badge, Button, Divider, Drawer, Group, Stack, Text, TextInput } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconQrcode, IconSend, IconUnlink } from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';
import { AdvancedSection } from '../common/AdvancedSection';
import { MessageDeliveryPanel } from './MessageDeliveryPanel';
import { ManualWhatsAppForm } from '../onboarding/ManualWhatsAppForm';
import { WhatsAppQrModal } from '../whatsapp/WhatsAppQrModal';

/**
 * One channel, one drawer.
 *
 * Tier 1 is the identity and health a vendor actually asks about, written in
 * words rather than Meta's enums: "High quality — Meta is happy with how people
 * react", not `qualityRating: GREEN`. Tier 2 — the raw ids, their own Meta app,
 * message delivery — sits inside `AdvancedSection`, collapsed, and opens itself
 * only when something in there needs fixing.
 */
export type ManageChannel = 'whatsapp' | 'facebook' | 'instagram';

export interface ManageDrawerProps {
  opened: boolean;
  onClose: () => void;
  channel: ManageChannel | null;
  onChanged: () => void;
}

const QUALITY_COPY: Record<string, string> = {
  GREEN: 'High — Meta is happy with how people react to your messages',
  YELLOW: 'Medium — some people have blocked or reported you recently',
  RED: 'Low — Meta may limit how many people you can message',
};

const SOURCE_COPY: Record<string, string> = {
  embedded_signup: 'automatically',
  coexistence: 'from your phone',
  manual: 'manually',
  user_oauth: 'through the older connection flow',
};

const STATUS_COPY: Record<string, string> = {
  active: 'Working — sending and receiving',
  pending_verification: 'Meta is still verifying this number',
  flagged: 'Flagged by Meta — messaging may be limited',
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" wrap="nowrap" align="flex-start" gap="xl">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm" ta="right">
        {value}
      </Text>
    </Group>
  );
}

export function ManageDrawer({ opened, onClose, channel, onChanged }: ManageDrawerProps) {
  const [qrOpen, setQrOpen] = useState(false);
  const [testTo, setTestTo] = useState('');
  const utils = trpc.useUtils();

  const status = trpc.integrations.meta.status.useQuery();
  const manualCfg = trpc.integrations.meta.getWhatsAppManualConfig.useQuery(undefined, {
    enabled: opened && channel === 'whatsapp',
  });

  const sendTest = trpc.integrations.meta.sendWhatsAppTestMessage.useMutation({
    onSuccess: () => notifications.show({ message: 'Test message sent', color: 'green' }),
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const disconnectWa = trpc.integrations.meta.disconnectWhatsAppNumber.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Disconnected', color: 'green' });
      void utils.integrations.meta.status.invalidate();
      onChanged();
      onClose();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const wa = status.data?.whatsapp[0] ?? null;
  const fb = status.data?.facebook[0] ?? null;
  const ig = status.data?.instagram[0] ?? null;

  const title =
    channel === 'whatsapp'
      ? `WhatsApp${wa?.displayPhoneNumber ? ` · ${wa.displayPhoneNumber}` : ''}`
      : channel === 'facebook'
        ? `Messenger${fb?.pageName ? ` · ${fb.pageName}` : ''}`
        : `Instagram${ig?.username ? ` · @${ig.username}` : ''}`;

  // Auto-open Advanced only when something inside it needs fixing — never
  // merely because saved manual config exists.
  const needsAttention =
    channel === 'whatsapp' &&
    ((wa?.source === 'manual' && manualCfg.data && !manualCfg.data.hasBYOCAppSecret) ||
      wa?.webhookSubscribed === false);

  const confirmDisconnect = () => {
    if (!wa) return;
    modals.openConfirmModal({
      title: 'Disconnect this number?',
      children: (
        <Text size="sm">
          You&apos;ll stop receiving messages on {wa.displayPhoneNumber ?? 'this number'} until you
          reconnect it. Your chat history stays.
        </Text>
      ),
      labels: { confirm: 'Disconnect', cancel: 'Keep it connected' },
      confirmProps: { color: 'red' },
      onConfirm: () => disconnectWa.mutate({ phoneNumberId: wa.phoneNumberId }),
    });
  };

  return (
    <>
      <Drawer opened={opened} onClose={onClose} position="right" size="lg" title={title}>
        {channel === 'whatsapp' && wa && (
          <Stack gap="md">
            <Stack gap="xs">
              <Field label="Status" value={STATUS_COPY[wa.status] ?? wa.status} />
              <Field label="Business" value={wa.verifiedName ?? 'Not set'} />
              {wa.qualityRating && (
                <Field label="Quality" value={QUALITY_COPY[wa.qualityRating] ?? wa.qualityRating} />
              )}
              <Field
                label="Connected"
                value={`${wa.connectedAt ? new Date(wa.connectedAt).toLocaleDateString() : 'Unknown'} · ${
                  SOURCE_COPY[wa.source] ?? wa.source
                }`}
              />
            </Stack>

            <Group align="flex-end" gap="xs" wrap="nowrap">
              <TextInput
                flex={1}
                size="xs"
                label="Send a test message"
                placeholder="+91 98765 43210"
                value={testTo}
                onChange={(e) => setTestTo(e.currentTarget.value)}
              />
              <Button
                size="xs"
                variant="light"
                leftSection={<IconSend size={14} />}
                loading={sendTest.isPending}
                disabled={!/^\+?[1-9]\d{6,14}$/.test(testTo.trim())}
                onClick={() => sendTest.mutate({ to: testTo.trim() })}
              >
                Send
              </Button>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconQrcode size={14} />}
                onClick={() => setQrOpen(true)}
              >
                QR code
              </Button>
            </Group>

            <AdvancedSection
              description="Raw identifiers, your own Meta app, and where messages are delivered."
              defaultOpen={!!needsAttention}
              badge={
                needsAttention ? (
                  <Badge size="sm" color="orange" variant="light">
                    Needs attention
                  </Badge>
                ) : undefined
              }
            >
              <Stack gap="md">
                <ManualWhatsAppForm onConnected={onChanged} />
                <Divider />
                <MessageDeliveryPanel channel="whatsapp" onChanged={onChanged} />
              </Stack>
            </AdvancedSection>

            <Divider />
            <Group justify="flex-end">
              <Button
                size="xs"
                color="red"
                variant="subtle"
                leftSection={<IconUnlink size={14} />}
                onClick={confirmDisconnect}
              >
                Disconnect
              </Button>
            </Group>
          </Stack>
        )}

        {channel === 'facebook' && fb && (
          <Stack gap="md">
            <Stack gap="xs">
              <Field label="Page" value={fb.pageName ?? fb.pageId} />
              {fb.category && <Field label="Category" value={fb.category} />}
              <Field
                label="Status"
                value={
                  fb.webhookSubscribedAt ? 'Working — receiving messages' : 'Not receiving messages'
                }
              />
              <Field
                label="Connected"
                value={fb.connectedAt ? new Date(fb.connectedAt).toLocaleDateString() : 'Unknown'}
              />
            </Stack>
            <AdvancedSection description="Where messages are delivered.">
              <MessageDeliveryPanel channel="facebook" onChanged={onChanged} />
            </AdvancedSection>
          </Stack>
        )}

        {channel === 'instagram' && ig && (
          <Stack gap="md">
            <Stack gap="xs">
              <Field label="Account" value={ig.username ? `@${ig.username}` : ig.igUserId} />
              {ig.name && <Field label="Name" value={ig.name} />}
              <Field
                label="Status"
                value={
                  ig.webhookSubscribedAt ? 'Working — receiving messages' : 'Not receiving messages'
                }
              />
              <Field
                label="Connected"
                value={ig.connectedAt ? new Date(ig.connectedAt).toLocaleDateString() : 'Unknown'}
              />
            </Stack>
            <AdvancedSection description="Where messages are delivered.">
              <MessageDeliveryPanel channel="instagram" onChanged={onChanged} />
            </AdvancedSection>
          </Stack>
        )}
      </Drawer>

      <WhatsAppQrModal opened={qrOpen} onClose={() => setQrOpen(false)} />
    </>
  );
}
